import { useState } from "react";

import type { EventFeedback } from "../../services/eventService";
import { ClientRegister, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { humanise } from "../register/registerLogic";
import { RATINGS, day, feedbackConfig, labelFrom, stars } from "./eventsLogic";

const rated = (value?: number | null) => (value ? `${stars(value)} ${labelFrom(RATINGS, String(value))}` : undefined);

/** What people said about events: all of it for staff, their own for families. */
export function FeedbackPanel({ feedback, isLoading, isError, onRetry, canManage }: {
  feedback?: EventFeedback[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
}) {
  const [viewing, setViewing] = useState<number | null>(null);
  const columns: RegisterColumn<EventFeedback>[] = [
    { header: "Event date", cell: (row) => day(row.event_start_date), exportValue: (row) => day(row.event_start_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Event", cell: (row) => <span className="font-medium text-gray-900">{row.event_name}</span>, exportValue: (row) => row.event_name, className: "min-w-[12rem]" },
    ...(canManage ? [{ header: "From", cell: (row: EventFeedback) => `${row.user_name} (${humanise(row.user_type)})`, exportValue: (row: EventFeedback) => row.user_name, className: "text-gray-700" }] : []),
    { header: "Rating", cell: (row) => <span className="text-amber-600" title={`${row.overall_rating} of 5`}>{stars(row.overall_rating)}</span>, exportValue: (row) => row.overall_rating, className: "whitespace-nowrap" },
    { header: "Comments", cell: (row) => row.comments || "—", exportValue: (row) => row.comments ?? "", className: "max-w-[22rem] truncate text-gray-700" },
    { header: "Would recommend", cell: (row) => (row.would_recommend ? "Yes" : "No"), exportValue: (row) => (row.would_recommend ? "Yes" : "No"), className: "text-gray-700" },
    { header: "Suggestions", cell: (row) => row.suggestions ?? "", exportValue: (row) => row.suggestions ?? "", exportOnly: true },
    { header: "Would come again", cell: (row) => (row.would_attend_again ? "Yes" : "No"), exportValue: (row) => (row.would_attend_again ? "Yes" : "No"), exportOnly: true },
  ];
  const row = viewing !== null ? feedback?.find((item) => item.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <ClientRegister
        title="Feedback"
        description={canManage ? "What people who registered thought of each event. Open one to read it in full." : "What you said about events you went to. Give feedback from My registrations."}
        rows={feedback}
        config={feedbackConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        noun={["response", "responses"]}
        exportName={{ file: "event-feedback", title: "Event Feedback" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No feedback yet. It opens to people who registered once an event has taken place." : "You have not given feedback yet."}
      />
      {row && (
        <DetailsModal
          title={row.event_name}
          headline={{ label: canManage ? `${row.user_name} · ${humanise(row.user_type)}` : "Your feedback", value: `${row.overall_rating} of 5`, note: labelFrom(RATINGS, String(row.overall_rating)) }}
          facts={[
            ["Event date", day(row.event_start_date)],
            ["Given on", day(row.created_at)],
            ["How it was organised", rated(row.organization_rating)],
            ["What happened on the day", rated(row.content_rating)],
            ["The venue", rated(row.venue_rating)],
            ["Would come again", row.would_attend_again ? "Yes" : "No"],
            ["Would recommend it", row.would_recommend ? "Yes" : "No"],
          ]}
          sections={[
            { title: "Comments", body: row.comments },
            { title: "Suggestions for next time", body: row.suggestions },
          ]}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
